import * as d3 from "d3";
import { isBrowser } from "../../helpers/utils";
import lodash from 'lodash';
import { BREAKPOINTS } from "../../constants/breakpoints.constants";

export const displayTrendRadarChart = (config) => {

  config.scale = config.scale || 1;
  const totalStrokeWidth = (config.size.segmentNameSectionStrokeWidth + 2) * 2 // +2px is for some additional space
  config.width = (config.radius * 2) + totalStrokeWidth / 2
  config.height = (config.radius * 2) + totalStrokeWidth / 2
  const scaled_width = Math.ceil(config.width * config.scale);
  const scaled_height = Math.ceil(config.height * config.scale);

  const svg = d3.select("svg#" + config.svg_id)
    .style("background-color", config.colors.background)
    .attr("width", 0)
    .attr("height", 0);

  const wrapperEle = svg.append("g")
  const radar = wrapperEle.append("g");

  // to place the circle in middle
  wrapperEle.attr("transform", translate(scaled_width / 2, scaled_height / 2));
  radar.attr("transform", `scale(${0})`)

  function resize(width) {
    let space = 80; // to remove margins and scroll bar
    if (width >= BREAKPOINTS.lg) {
      space = 180
    }
    const scale = Math.min(((width - space) / (config.radius * 2)), 1)
    const w = Math.ceil(config.width * scale)
    const h = Math.ceil(config.height * scale)
    svg.transition().delay(200)
      .attr("width", w)
      .attr("height", h)

    wrapperEle.attr("transform", `${translate(w / 2, h / 2)}`)
    radar.transition().delay(700).attr("transform", `scale(${scale})`)
  }

  if (isBrowser()) {
    resize(window.screen.width)
  }


  const deg = (360 / config.categories.length)
  // Calculating categories start and end angle  
  if (config.categories.length > 1) {

    for (let i = 0; i < config.categories.length; i++) {
      const category = config.categories[i]

      if (i === 0) {
        category.arcStartAngle = 360 - (deg / 2)
        category.arcEndAngle = 360 + (deg / 2)
      } else {
        category.arcStartAngle = config.categories[i - 1]?.arcEndAngle % 360
        category.arcEndAngle = (category.arcStartAngle + deg)
      }

    }
  }


  // to draw rings
  for (let r = config.rings.length - 1; r >= 0; r--) {
    const ring = config.rings[r]
    if (r !== config.rings.length - 1) {
      radar.append("circle").attr("cx", 0)
        .attr("cy", 0)
        .attr("r", ring.radius)
        .style("fill", ring.bgColor);
    }

    const ringArc = d3.arc()
      .startAngle(degreeToRadian(config.categories.at(0).arcStartAngle))
      .endAngle(degreeToRadian(config.categories.at(0).arcEndAngle))
      .innerRadius(ring.radius - 12).outerRadius(ring.radius)


    radar.append("g").append("path")
      .attr("class", "ringArcSlices")
      .attr("d", ringArc)
      .attr("fill", "none") //bg color of the category arc
      .attr("stroke", "none")
      .attr("stroke-width", "0px")
      .each(function () { createCurvedPath.call(this, "ringArc", r) });

    radar.append("text")
      .attr("class", "ringText")
      .attr("dy", config.size.ringNameFontSize) //distance from top
      .append("textPath")
      .style("font-size", `${config.size.ringNameFontSize}px`)
      .style("letter-spacing", `${config.size.ringNameLetterSpacing}px`)
      .style("font-weight", config.size.ringNameFontWeight)
      .attr("fill", config.colors.ringNameFontColor)
      .attr("startOffset", "50%")
      .style("text-anchor", "middle")
      .attr("xlink:href", "#ringArc" + r)
      .text(ring.name);
  }

  const cornerRadius = 10
  const arc = d3.arc().innerRadius(config.rings.at(-1).radius).outerRadius((config.radius)).cornerRadius(cornerRadius);
  // Creating categories and placing its entries/points into it.
  config.categories.forEach((category, i) => {

    // Appending group tag to create category
    const categoryEle = radar.append("g").attr("class", "segment")

    // Registering events with callbacks for category elements
    if (BREAKPOINTS.isLarge()) {
      categoryEle
        .on("mouseenter", (e) => {
          highlightCategory(category)
        }).on("mouseleave", (e) => {
          unHighlightCategory(category)
        })
    } else {
      categoryEle.on("click", (e) => {
        config.changeSection(i)
      })
    }

    // Appending path to draw cone shape for category
    categoryEle.append("path")
      .attr("class", "segment-path")
      .attr("d", () => d3.arc()
        .innerRadius(0)
        .outerRadius(config.radius)
        .startAngle(degreeToRadian(category.arcStartAngle))
        .endAngle(degreeToRadian(category.arcEndAngle))
        .cornerRadius(cornerRadius)()
      )
      .attr('stroke', config.colors.categoryLine)
      .attr('stroke-width', "1px")
      .attr("pointer-events", "visible") // to get events
      .attr('fill', "none")


    // to add segment names on top in curved
    // reference: https://www.visualcinnamon.com/2015/09/placing-text-on-arcs/
    const segmentNameGroup = categoryEle.append("g")

    if (BREAKPOINTS.isLarge()) {
      segmentNameGroup
        .on("click", (e) => {
          config.changeSection(i)
          e.stopPropagation()
        })
        .on("mouseover", function () {
          d3.select(this).selectAll("text")
            .style("opacity", 0.3)
        })
        .on("mouseout", function () {
          d3.select(this).selectAll("text")
            .style("opacity", 1)
        })
    }

    segmentNameGroup.append("path")
      .attr("class", "segmentArcSlices")
      .attr("d", arc.startAngle(degreeToRadian(category.arcStartAngle)).endAngle(degreeToRadian(category.arcEndAngle))())
      .attr("fill", category.bgColor) //bg color of the category arc
      .attr("stroke", category.textColor)
      .attr("stroke-width", `${config.size.segmentNameSectionStrokeWidth}px`)
      .attr("pointer-events", "visible") // to get events
      .style("cursor", "pointer")
      .each(function () { createCurvedPath.call(this, "segmentArc", i, category.flipText) });

    // placing segment name
    const sentence = category.name.split("\n")
    let fontSize = config.size.categoryFontSizeMobile
    let letterSpacing = config.size.categoryLetterSpacingMobile
    let fontWeight = config.size.categoryFontWeightMobile
    let lineSpace = 10
    if (BREAKPOINTS.isLarge()) {
      fontSize = config.size.categoryFontSizeDesktop
      letterSpacing = config.size.categoryLetterSpacingDesktop
      fontWeight = config.size.categoryFontWeightDesktop
      lineSpace = 5
    }
    const textHeight = fontSize * .7
    const categoryNameSectionSpace = config.radius - config.rings.at(-1).radius
    const contentHeight = (textHeight * sentence.length) + (lineSpace * (sentence.length - 1))
    const topAndBottomSpace = categoryNameSectionSpace - contentHeight
    let dy = textHeight + topAndBottomSpace / 2

    for (let senLen = 0; senLen < sentence.length; senLen++) {
      segmentNameGroup.append("text")
        .attr("class", "segment-name")
        .style("font-size", `${fontSize}px`)
        .style("font-weight", fontWeight)
        .style("letter-spacing", `${letterSpacing}px`)
        .attr("dy", category.flipText ? - categoryNameSectionSpace + dy : dy) //distance from top
        .append("textPath")
        .attr("fill", category.textColor)
        .attr("startOffset", "50%")
        .style("text-anchor", "middle")
        .style("cursor", "pointer")
        .attr("xlink:href", () => "#segmentArc" + i)
        .text(sentence[senLen])
      dy += textHeight + lineSpace
    }



    if (i === 0) {
      const hiddenWeightLabel = radar.append("text")
        .style("font-size", `${config.size.weightLabelFontSize}px`)
        .style("font-weight", config.size.weightLabelFontWeight)
        .style("letter-spacing", `${config.size.weightLabelLetterSpacing}px`)
        .attr("fill", "none")
        .attr("pointer-events", "none")
        .text(config.weightLabel)

      const defs = categoryEle.append("defs")
      defs.append("marker")
        .attr("id", "arrow")
        .attr("markerWidth", 5)
        .attr("markerHeight", 5)
        .attr("refX", 5)
        .attr("refY", 2.5)
        .attr("orient", "auto-start-reverse")
        .append("path")
        .attr("d", "M 0 0 L 5 2.5 L 0 5")
        .attr("fill", "none")
        .attr("stroke", config.colors.ringNameFontColor)
        .attr("stroke-width", "1px")

      const observer = new ResizeObserver(() => {
        const textBBox = hiddenWeightLabel.node().getBBox()
        const rightAndLeftSpace = 14
        const betweenLineAndTextSpace = 6
        const angle = category.arcStartAngle - 90  // angle starts from right middle. need to make it from top middle that's why -90
        const pathPoints = cartesian({ r: config.rings.at(-1).radius, t: angle })
        const firstPoints = cartesian({ r: rightAndLeftSpace, t: angle })
        const secondPoints = cartesian({ r: ((config.rings.at(-1).radius) / 2) - ((textBBox.width + betweenLineAndTextSpace) / 2), t: angle })
        const thirdPoints = cartesian({ r: ((config.rings.at(-1).radius) / 2) + ((textBBox.width + betweenLineAndTextSpace) / 2), t: angle })
        const endPoints = cartesian({ r: config.rings.at(-1).radius - rightAndLeftSpace, t: angle })

        // Adding hidden path to place text
        categoryEle.select("#hiddenWeightLabelPath").remove()
        categoryEle.append("path")
          .attr("d", `M${pathPoints.x},${pathPoints.y} L0,0`)
          .attr("id", "hiddenWeightLabelPath")
          .attr("fill", "none")

        // adding g element to include text and path elements and then to move together
        categoryEle.select("#weightLabelGroup").remove()
        const gTag = categoryEle
          .append("g")
          .attr("id", "weightLabelGroup")
          .style("transform", `translate(6px,-6px)`)

        // Creating line with arrow
        gTag.append("path")
          .attr("d", `M${firstPoints.x},${firstPoints.y} L${secondPoints.x},${secondPoints.y} M${thirdPoints.x},${thirdPoints.y} L${endPoints.x},${endPoints.y}`)
          .attr("fill", "none")
          .attr("stroke", config.colors.ringNameFontColor)
          .attr("stroke-width", "1px")
          .attr("marker-start", "url(#arrow)")
          .attr("marker-end", "url(#arrow)")


        gTag.append("text")
          .style("font-size", `${config.size.weightLabelFontSize}px`)
          .style("font-weight", config.size.weightLabelFontWeight)
          .style("letter-spacing", `${config.size.weightLabelLetterSpacing}px`)
          .attr("pointer-events", "none")
          .attr("dy", 4) //distance from top
          .append("textPath")
          .attr("fill", config.colors.ringNameFontColor)
          .attr("startOffset", "50%")
          .style("text-anchor", "middle")
          .attr("xlink:href", () => "#hiddenWeightLabelPath")
          .text(config.weightLabel)
      })
      observer.observe(hiddenWeightLabel.node())
    }


    // To place entries in its categories  
    category.entries?.forEach((entry, entryIndex) => {

      // Appending group tag to append circle and text inside of it.
      const entryEle = categoryEle.append("g").attr("class", "trend-container")

      entry.circlePoint = getXandY(entry.x, entry.y)
      entry.radius = config.entryWeight[entry.weight].radius
      // Appending circle element to place dot points
      const circleEle = entryEle.append("circle")
        .attr("class", "trend")
        .attr("cx", entry.circlePoint.x)
        .attr("cy", entry.circlePoint.y)
        .attr("r", entry.radius)
        .style("fill", config.categories[i].circleColor)
        .attr("stroke", config.categories[i].circleColor)
        .style("cursor", "pointer");

      if (window.innerWidth >= BREAKPOINTS.lg) {
        pulse(circleEle)
      }


      // config.size.trendLabelBoxSize is the label text box width size. To place it in middle dividing it by 2
      // To place text label below the circle -radius is used.
      const spacebtw = 3 // for extra space between the point and the label
      const textPoint = getXandY(entry.x - config.size.trendLabelBoxSize / 2, entry.y - entry.radius - spacebtw)
      // Appending foreignObject to use html element for wrap feature.
      const trendLabelBoxElem = entryEle.append("foreignObject")
        .attr("width", "1px") // "auto" is not working in firefox
        .attr("height", "1px")
        .style("overflow", "visible")
        .attr("x", textPoint.x).attr("y", textPoint.y)
        .append("xhtml:div")
        .attr("class", "radar-point-label-box")
        .style("color", config.colors.pointLabelText)
        .style("text-align", "center")
        .style("width", `${config.size.trendLabelBoxSize}px`)
        .style("height", "auto")
        .style("line-height", config.size.trendTextLineHeight)
        .style("font-size", `${config.size.trendLabelFontSize}px`)
        .style("font-weight", config.size.trendLabelFontWeight)
        .style("overflow", "visible")


      const trendLabelElem = trendLabelBoxElem.append("xhtml:span")
        .attr("class", "radar-point-label")
        .style("display", "inline-block")
        .style("text-align", "center")
        .style("margin-left", `auto`)
        .style("margin-right", "auto")
        .style("cursor", "pointer")
        .html(entry.label)

      if (BREAKPOINTS.isLarge()) {
        const trendMouseOver = () => {
          trendLabelElem
            // opacity style
            // .style("opacity", 0.3) // opacity property isn't working inside foreginobject element in safari
            .style("color", "rgba(0,0,0,0.3)")
        }
        const trendMouseOut = () => {
          trendLabelElem
            // opacity style
            // .style("opacity", 1)
            .style("color", "rgb(0,0,0)")
        }
        const trendClick = (e) => {
          config.changeSection(i, entryIndex)
          e.stopPropagation();
        }
        trendLabelElem
          .on("mouseover", trendMouseOver)
          .on("mouseout", trendMouseOut)
          .on("click", trendClick);

        circleEle
          .on("mouseover", trendMouseOver)
          .on("mouseout", trendMouseOut)
          .on("click", trendClick);
      }
    })
  })





  // pulse animation 
  function pulse(circle) {
    (function repeat() {
      circle
        .transition()
        .duration(500)
        .attr("stroke-width", "0px")
        .attr('stroke-opacity', 0)
        .transition()
        .duration(500)
        .attr("stroke-width", "0px")
        .attr('stroke-opacity', 0.5)
        .transition()
        .duration(1000)
        .attr("stroke-width", "20px")
        .attr('stroke-opacity', 0)
        .ease(d3.easeSin)
        .on("end", repeat);
    })();
  }



  function createCurvedPath(className, id, flip) {
    const firstArcSection = /(^.+?)L/;
    let newArc = firstArcSection.exec(d3.select(this).attr("d"))[1];
    newArc = newArc.replace(/,/g, " ");

    if (flip) {
      var startLoc = /A.*?0 0 1(.*?)A/,        //Everything between the capital M and first capital A
        middleLoc = /A.*?A(.*?)0 0 1/,    //Everything between the capital A and 0 0 1
        endLoc = /A.*?A.*?0 0 1(.*?)A/;   //Everything between the 0 0 1 and the end of the string (denoted by $)
      //Flip the direction of the arc by switching the start and end point (and sweep flag)
      var newStart = endLoc.exec(newArc)[1];
      var newEnd = startLoc.exec(newArc)[1];
      var middleSec = middleLoc.exec(newArc)[1];
      //Build up the new arc notation, set the sweep-flag to 0
      newArc = "M" + newStart + "A" + middleSec + "0 0 0 " + newEnd;
    }//if

    radar.append("path")
      .attr("class", "hiddenRingArcs")
      .attr("id", `${className}${id}`)
      .attr("d", newArc)
      .style("fill", "none");
  }



  // Appending path element for hightlighting category on hover and on click
  radar.append("path").attr("id", "hider").attr("fill", "#fff")
  const highLightAnimationDuration = 500 //ms
  // to hightlight a category on hover and on click
  function highlightCategory(d) {
    const end = d.arcEndAngle % 360
    // animating
    radar.select("#hider").style("opacity", .7).transition().duration(highLightAnimationDuration)
      .attrTween("d",
        function (d, i) {
          const interpolate = d3.interpolate(degreeToRadian(end), degreeToRadian(end + (deg * (config.categories.length - 1))));
          const arc = d3.arc()
            .innerRadius(0)
            .outerRadius(config.radius + totalStrokeWidth)
            .startAngle(degreeToRadian(end))
          return function (t) {
            arc.endAngle(interpolate(t));
            return arc();
          };
        }
      )
  }

  // to unhightlight category on mouse out 
  function unHighlightCategory(d) {
    radar.select("#hider")
      .transition()
      .delay(100)
      .style("opacity", 0)
      .transition().attr("d", "M0,0")
  }
}


function tween(start, end) {
  const i = d3.interpolate(start, end);
  return function (t) {
    return i(t);
  };
}


export function translate(x, y) {
  return "translate(" + x + "," + y + ")";
}


function getXandY(x, y) {
  const quadrants = [
    { x: 1, y: -1 },
    { x: 1, y: 1 },
    { x: -1, y: 1 },
    { x: -1, y: -1 }
  ];
  let q;
  if (x >= 0 && y >= 0) q = 0 // 1st category
  else if (x >= 0 && y <= 0) q = 1 // 2nd category
  else if (x <= 0 && y <= 0) q = 2 // 3rd category
  else if (x <= 0 && y >= 0) q = 3 // 4th qudarant
  return { x: Math.abs(x) * quadrants[q].x, y: Math.abs(y) * quadrants[q].y }
}

// To convert from degrees to radians, multiply the number of degrees by π/180.
function degreeToRadian(deg) {
  return deg * (Math.PI / 180)
}

// To convert Polar to cartesian points
function cartesian(polar) {
  const t = degreeToRadian(polar.t)
  return {
    x: (polar.r * Math.cos(t)),
    y: (polar.r * Math.sin(t))
  }
}